import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../../api/axios";
import { useProfileCardModal } from "../../context/ProfileCardContext";
import { buildEventPath } from "../../utils/eventUrl";
import { resolveUserAvatarSrc } from "../../utils/userAvatar";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import useChatVisualViewportStyle from "../../hooks/useDirectChatVisualViewport";
import { EventChatDesktopView } from "./ChatDesktopView";
import { EventChatMobileView } from "./ChatMobileView";
import ChatInputBar from "./ChatInputBar";

const EMPTY_OBJECT = {};

// An invitation is the read-only opening state of the ordinary event chat.
// The same views, message bubbles and input action slot are used after joining.
export default function ProfessionalMeetupInviteChat({ invitation, onBack, onJoined, onInitialReady, deferInputUntilReady = false }) {
  const { t, i18n } = useTranslation("menu");
  const navigate = useNavigate();
  const { openProfileCard } = useProfileCardModal();
  const isDesktop = useIsDesktop();
  const [joining, setJoining] = useState(false);
  const joiningRef = useRef(false);
  const [error, setError] = useState("");
  const headerRef = useRef(null);
  const feedRef = useRef(null);
  const inputPropsRef = useRef(null);
  const viewportStyle = useChatVisualViewportStyle(isDesktop, feedRef);
  const event = invitation.event || EMPTY_OBJECT;
  const eventId = event.id || invitation.event_id;
  const eventPath = buildEventPath(eventId, event.name);
  const organizer = invitation.organizer || EMPTY_OBJECT;
  const messageKey = invitation.relation === "can_help"
    ? "chats.professional_meetup.message_can_help"
    : invitation.relation === "both"
      ? "chats.professional_meetup.message_both"
      : "chats.professional_meetup.message_looking_for";

  useEffect(() => { onInitialReady?.(); }, [onInitialReady]);

  const join = useCallback(async () => {
    if (joiningRef.current) return;
    joiningRef.current = true;
    setJoining(true);
    setError("");
    try {
      const { data } = await api.post(`/events/join-event/${eventId}/`, {});
      if (data?.success && (data.joined === undefined || data.joined === true)) {
        onJoined?.();
        return;
      }
      setError(data?.error || t("chats.professional_meetup.join_failed"));
    } catch (requestError) {
      setError(requestError?.response?.data?.error || t("chats.professional_meetup.join_failed"));
    } finally {
      joiningRef.current = false;
      setJoining(false);
    }
  }, [eventId, onJoined, t]);

  const messageOrganizer = useCallback(() => {
    if (!organizer.code) return;
    void api.post(`/events/professional-meetup-invitations/${invitation.id}/organizer-chat-opened/`).catch(() => {});
    navigate(`/menu/chats/direct/code/${encodeURIComponent(organizer.code)}`, {
      state: { source: "event_details", peer: organizer, eventContext: { ...event, public_path: eventPath } },
    });
  }, [event, eventPath, invitation.id, navigate, organizer]);

  const inputProps = useMemo(() => ({
    t,
    language: i18n.resolvedLanguage || i18n.language,
    inputValue: "",
    canSendMessages: false,
    allowAttachments: false,
    hideComposerWhenDisabled: true,
    sendDisabledMessage: error,
    showTypingIndicator: false,
    primaryCtaLabel: t(joining ? "chats.professional_meetup.joining" : "chats.professional_meetup.join"),
    onPrimaryCtaClick: join,
    secondaryCtaLabel: organizer.code ? t("chats.professional_meetup.message_organizer") : "",
    onSecondaryCtaClick: messageOrganizer,
  }), [error, i18n.language, i18n.resolvedLanguage, join, joining, messageOrganizer, organizer.code, t]);
  inputPropsRef.current = inputProps;

  useEffect(() => {
    if (isDesktop || deferInputUntilReady) return undefined;
    window.dispatchEvent(new CustomEvent("vg-chat-input-open", {
      detail: { props: inputPropsRef.current, no_auto_expand: true, source: "chat" },
    }));
    return () => window.dispatchEvent(new CustomEvent("vg-chat-input-close"));
  }, [isDesktop, deferInputUntilReady, invitation.id]);
  useEffect(() => {
    if (isDesktop || deferInputUntilReady) return;
    window.dispatchEvent(new CustomEvent("vg-chat-input-update", {
      detail: { props: inputProps, no_auto_expand: true, source: "chat" },
    }));
  }, [inputProps, isDesktop, deferInputUntilReady]);

  const items = [{
    id: `meetup-invitation-${invitation.id}`,
    type: "message",
    message_type: "system_as_user",
    role: "host",
    author_is_staff: organizer.is_staff,
    sender_username: organizer.username || "vaultgrid",
    sender_code: organizer.code || "",
    avatarSrc: resolveUserAvatarSrc(organizer),
    created_at: invitation.created_at,
    text: invitation.message_text || t(messageKey, {
      topic: invitation.topic_label,
      profileReason: t(`chats.professional_meetup.profile_${invitation.relation || "looking_for"}`, {
        topic: invitation.topic_label,
        context: ["male", "female"].includes(invitation.recipient_gender) ? invitation.recipient_gender : undefined,
      }),
    }),
    is_self: false,
  }];
  const viewProps = {
    t,
    language: i18n.resolvedLanguage || i18n.language,
    headerRef,
    feedRef,
    viewportStyle,
    handleBackClick: onBack,
    openEventFromChatHeader: () => navigate(eventPath),
    eventId,
    eventName: event.name,
    eventType: event.type,
    eventDateTime: event.date_time,
    eventForCard: event,
    participantsCount: 0,
    participantsLabelOverride: t("chats.professional_meetup.invitation"),
    participantsDisabled: true,
    showParticipants: false,
    items,
    feedBottomPadPx: isDesktop ? 24 : 180,
    openProfileByCode: openProfileCard,
    inputBar: <ChatInputBar {...inputProps} embedded />,
  };

  return isDesktop ? <EventChatDesktopView {...viewProps} /> : <EventChatMobileView {...viewProps} />;
}
